// WebSocket Client Service for Real-time Collaboration
// Handles real-time events for collaboration, AI suggestions, and voice commands
// Migrated from Socket.IO to native WebSocket

import React from 'react';
import { getBrowserWsBaseUrl } from '@/config/apiEnv';
import { useAuthStore } from '@/stores/authStore';

/** Same token source as REST clients (auth store + legacy localStorage key). */
function getAuthToken(): string | null {
  return useAuthStore.getState().tokens?.token || localStorage.getItem('authToken');
}

/** Seconds; tolerate skew between device and issuer clocks. */
const JWT_EXP_SKEW_SEC = 30;

/** Return JWT `exp` (Unix seconds), or null if missing or not parseable. */
function readJwtExpSeconds(token: string): number | null {
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = (4 - (b64.length % 4)) % 4;
    b64 += '='.repeat(pad);
    const payload = JSON.parse(atob(b64)) as { exp?: unknown };
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

function hintForEarlyClose(code: number, reason: string, sawOpen: boolean): string | undefined {
  if (sawOpen) return undefined;
  const r = (reason || '').toLowerCase();
  if (code === 1008 || r.includes('policy')) {
    return 'Connection blocked before open (policy). Check server origin / WebSocket rules.';
  }
  if (code === 1002 || code === 1006 || code === 1015) {
    return 'Handshake failed before open. Typical causes: API not reachable (Vite proxies /api to your Go server, often :8080), or HTTP 401/403 on upgrade (try signing in again). Inspect this request in DevTools → Network → WS for the HTTP status.';
  }
  return undefined;
}

interface CursorUpdate {
  sessionId: string;
  userId: string;
  cursor: {
    x: number;
    y: number;
    viewport_width: number;
    viewport_height: number;
    scroll_x: number;
    scroll_y: number;
    active_element?: string;
    hovered_element?: string;
  };
  activity: 'idle' | 'scrolling' | 'clicking' | 'typing' | 'selecting';
}

interface AnnotationEvent {
  sessionId: string;
  userId: string;
  annotation: {
    id: string;
    type: 'highlight' | 'comment' | 'drawing' | 'bookmark' | 'tag';
    targetElementSelector?: string;
    content: string;
    color?: string;
    position?: { x: number; y: number; width: number; height: number };
  };
}

interface ChatMessage {
  sessionId: string;
  userId: string;
  content: string;
  annotationId?: string;
  mentions?: string[];
}

interface VoiceCommandEvent {
  userId: string;
  transcript: string;
  command: string;
  confidence: number;
  action?: string;
  target?: string;
}

interface SuggestionEvent {
  type: 'tab_suggestion' | 'group_suggestion' | 'action_suggestion';
  data: any;
  confidence: number;
  reasoning: string;
}

interface GenerationProgressEvent {
  session_id: string;
  stage: string;
  percent: number;
  message: string;
}

interface GenerationCompletedEvent {
  app_id: string;
  session_id: string;
  app_type: string;
  app_name?: string;
  message: string;
}

interface GenerationFailedEvent {
  session_id: string;
  error: string;
}

type EventCallback = (...args: any[]) => void;

interface WSMessage {
  type: string;
  [key: string]: any;
}

class WebSocketService {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private eventHandlers: Map<string, Set<EventCallback>> = new Map();
  private connectionState: 'disconnected' | 'connecting' | 'connected' = 'disconnected';
  private messageQueue: WSMessage[] = [];
  private wsUrl: string = '';
  private connectPromise: Promise<void> | null = null;

  constructor() {
    // No setup needed for native WebSocket
  }

  /** Browsers cannot send Authorization on WebSocket; Go API accepts ?token= for WS upgrade. */
  private buildWsUrl(base: string): string {
    let u: URL;
    try {
      u = new URL(base);
    } catch {
      return base;
    }
    const token = getAuthToken();
    if (token) {
      u.searchParams.set('token', token);
    }
    return u.toString();
  }

  connect(url?: string): Promise<void> {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.connectionState = 'connected';
      return Promise.resolve();
    }
    if (this.connectPromise) {
      return this.connectPromise;
    }

    this.connectPromise = new Promise((resolve, reject) => {
      // Drop stale sockets before opening a new connection.
      if (this.ws && this.ws.readyState !== WebSocket.CLOSED) {
        try {
          this.ws.close();
        } catch {
          /* ignore */
        }
        this.ws = null;
      }

      const base = url && !url.includes('?token=')
        ? url
        : `${getBrowserWsBaseUrl()}/api/v1/ws`;
      this.wsUrl = this.buildWsUrl(base);

      const token = getAuthToken();
      if (token) {
        const exp = readJwtExpSeconds(token);
        if (exp !== null && exp <= Math.floor(Date.now() / 1000) + JWT_EXP_SKEW_SEC) {
          const iso = new Date(exp * 1000).toISOString();
          const msg = `WebSocket skipped: auth token expired at ${iso}. Sign in again.`;
          console.warn(msg);
          this.connectionState = 'disconnected';
          this.emit('connection_status', {
            connected: false,
            error: 'token_expired',
            hint: msg,
            tokenExp: iso,
          });
          reject(new Error(msg));
          return;
        }
      } else {
        console.warn(
          'WebSocket: no auth token (sign in to enable live sync).'
        );
        this.connectionState = 'disconnected';
        this.emit('connection_status', {
          connected: false,
          error: 'no_token',
          hint: 'Sign in to enable live sync.',
        });
        reject(new Error('WebSocket: not signed in'));
        return;
      }

      let connectFinished = false;
      let sawOpen = false;
      const finishConnect = (fn: () => void) => {
        if (connectFinished) return;
        connectFinished = true;
        this.connectPromise = null;
        fn();
      };

      this.connectionState = 'connecting';

      try {
        this.ws = new WebSocket(this.wsUrl);

        this.ws.onopen = () => {
          sawOpen = true;
          console.log('WebSocket connected');
          this.connectionState = 'connected';
          this.reconnectAttempts = 0;
          this.emit('connection_status', { connected: true });

          // Flush queued messages
          while (this.messageQueue.length > 0) {
            const msg = this.messageQueue.shift();
            if (msg) this.send(msg);
          }

          finishConnect(() => resolve());
        };

        this.ws.onerror = (error) => {
          console.error(
            'WebSocket error (details are limited in browsers; see following close log):',
            error
          );
          this.connectionState = 'disconnected';
          this.emit('connection_status', {
            connected: false,
            error: 'connection error',
            hint: 'If the socket never opened, check the close event log below and Network → WS for HTTP status on the handshake.',
          });
          finishConnect(() => reject(error));
        };

        this.ws.onclose = (event) => {
          const detail = `code=${event.code} reason="${event.reason || ''}" wasClean=${event.wasClean}`;
          console.log('WebSocket closed:', detail);
          this.connectionState = 'disconnected';
          const hint = hintForEarlyClose(event.code, event.reason, sawOpen);
          if (hint) console.warn('WebSocket:', hint);
          this.emit('connection_status', {
            connected: false,
            closeCode: event.code,
            closeReason: event.reason,
            wasClean: event.wasClean,
            hadOpened: sawOpen,
            ...(hint ? { hint } : {}),
          });

          // Attempt reconnection
          if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this.scheduleReconnect();
          } else {
            this.connectionState = 'disconnected';
            this.emit('connection_status', { connected: false, error: 'max_reconnect_attempts' });
          }
        };

        this.ws.onmessage = (event) => {
          try {
            const msg: WSMessage = JSON.parse(event.data);
            this.handleMessage(msg);
          } catch (error) {
            console.error('Failed to parse WebSocket message:', error);
          }
        };
      } catch (error) {
        this.connectionState = 'disconnected';
        this.connectPromise = null;
        reject(error);
      }
    });

    return this.connectPromise;
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

    console.log(`Scheduling reconnect attempt ${this.reconnectAttempts} in ${delay}ms`);

    this.reconnectTimer = setTimeout(() => {
      void this.connect().catch(() => {});
    }, delay);
  }

  private handleMessage(msg: WSMessage): void {
    // Forward events based on message type
    switch (msg.type) {
      // Collaboration events
      case 'user_joined':
        this.emit('user_joined', msg.data);
        break;
      case 'user_left':
        this.emit('user_left', msg.data);
        break;
      case 'session_state':
        this.emit('session_state', msg.data);
        break;
      case 'cursor_update':
        this.emit('cursor_update', msg.data);
        break;
      case 'annotation_created':
        this.emit('annotation_created', msg.data);
        break;
      case 'annotation_resolved':
        this.emit('annotation_resolved', msg.data);
        break;
      case 'chat_message':
        this.emit('chat_message', msg.data);
        break;
      case 'reaction_added':
        this.emit('reaction_added', msg.data);
        break;
      case 'user_typing':
        this.emit('user_typing', msg.data);
        break;
      case 'user_navigated':
        this.emit('user_navigated', msg.data);
        break;
      case 'mention_notification':
        this.emit('mention_notification', msg.data);
        break;

      // Voice command events
      case 'voice_command_processed':
        this.emit('voice_command_processed', msg.data);
        break;
      case 'voice_command_result':
        this.emit('voice_command_result', msg.data);
        break;

      // Suggestion events
      case 'suggestion_update':
        this.emit('suggestion_update', msg.data);
        break;
      case 'proactive_suggestion':
        this.emit('proactive_suggestion', msg.data);
        break;

      // Shared workspace events — broadcast to all clients, the page
      // filters by workspace_id and ignores events for workspaces the
      // current user does not belong to.
      case 'workspace_created':
        this.emit('workspace_created', msg.data);
        break;
      case 'workspace_updated':
        this.emit('workspace_updated', msg.data);
        break;
      case 'workspace_deleted':
        this.emit('workspace_deleted', msg.data);
        break;
      case 'workspace_member_added':
        this.emit('workspace_member_added', msg.data);
        break;
      case 'workspace_member_removed':
        this.emit('workspace_member_removed', msg.data);
        break;
      case 'workspace_member_role_changed':
        this.emit('workspace_member_role_changed', msg.data);
        break;
      case 'workspace_ownership_transferred':
        this.emit('workspace_ownership_transferred', msg.data);
        break;
      case 'workspace_tab_assigned':
        this.emit('workspace_tab_assigned', msg.data);
        break;
      case 'workspace_tab_unassigned':
        this.emit('workspace_tab_unassigned', msg.data);
        break;
      case 'workspace_invitation_created':
        this.emit('workspace_invitation_created', msg.data);
        break;
      case 'workspace_heartbeat':
        this.emit('workspace_heartbeat', msg.data);
        break;

      // Generation events
      case 'generation_started':
        this.emit('generation_started', msg.data);
        break;
      case 'generation_progress':
        this.emit('generation_progress', msg.data);
        break;
      case 'generation_completed':
        this.emit('generation_completed', msg.data);
        break;
      case 'generation_failed':
        this.emit('generation_failed', msg.data);
        break;

      // Pong response
      case 'pong':
        // Heartbeat response, no-op
        break;

      case 'llm_chat_response':
        this.emit('llm_chat_response', msg.data ?? msg);
        break;

      // Notification from server (real-time notification center)
      case 'notification':
        if (msg.data?.error) {
          this.emit('error', msg.data.error);
        } else {
          this.emit('notification', msg.data);
        }
        break;

      case 'tab_sync':
      case 'tab_update':
        this.emit(msg.type, msg.data);
        break;

      default:
        console.log('Unknown WebSocket message type:', msg.type);
    }
  }

  private send(msg: WSMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    } else {
      // Queue message if not connected
      this.messageQueue.push(msg);
    }
  }

  /** JSON envelope `{ type, data }` matching Go `internal/websocket.Message`. */
  sendTypedMessage(type: string, data: Record<string, unknown>): void {
    this.send({ type, data } as WSMessage);
  }

  disconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.connectionState = 'disconnected';
    this.messageQueue = [];
  }

  // Event subscription
  on(event: string, callback: EventCallback): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event)?.add(callback);
  }

  off(event: string, callback: EventCallback): void {
    this.eventHandlers.get(event)?.delete(callback);
  }

  public emit(event: string, data: any): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in event handler for ${event}:`, error);
        }
      });
    }
  }

  // Connection state
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  getConnectionState(): 'disconnected' | 'connecting' | 'connected' {
    if (this.ws?.readyState === WebSocket.OPEN) {
      return 'connected';
    }
    if (this.ws?.readyState === WebSocket.CONNECTING) {
      return 'connecting';
    }
    return this.connectionState === 'connected' ? 'disconnected' : this.connectionState;
  }

  // Collaboration methods
  joinSession(sessionId: string, userId: string, displayName: string): void {
    this.send({ type: 'join_session', sessionId, userId, displayName });
  }

  leaveSession(sessionId: string, userId: string): void {
    this.send({ type: 'leave_session', sessionId, userId });
  }

  sendCursorUpdate(data: CursorUpdate): void {
    this.send({ type: 'cursor_update', ...data });
  }

  createAnnotation(data: AnnotationEvent): void {
    this.send({ type: 'annotation_created', ...data });
  }

  resolveAnnotation(annotationId: string, sessionId: string, userId: string): void {
    this.send({ type: 'annotation_resolved', annotationId, sessionId, userId });
  }

  sendChatMessage(data: ChatMessage): void {
    this.send({ type: 'chat_message', ...data });
  }

  addReaction(commentId: string, sessionId: string, userId: string, emoji: string): void {
    this.send({ type: 'add_reaction', commentId, sessionId, userId, emoji });
  }

  sendTypingStart(sessionId: string, userId: string): void {
    this.send({ type: 'typing_start', sessionId, userId });
  }

  sendTypingStop(sessionId: string, userId: string): void {
    this.send({ type: 'typing_stop', sessionId, userId });
  }

  navigatePage(sessionId: string, url: string, userId: string): void {
    this.send({ type: 'page_navigate', sessionId, url, userId });
  }

  // Voice command methods
  sendVoiceCommand(data: VoiceCommandEvent): void {
    this.send({ type: 'voice_command', ...data });
  }

  // Suggestion methods
  subscribeToSuggestions(userId: string): void {
    this.send({ type: 'subscribe_suggestions', userId });
  }

  unsubscribeFromSuggestions(userId: string): void {
    this.send({ type: 'unsubscribe_suggestions', userId });
  }

  acknowledgeSuggestion(suggestionId: string, action: 'accept' | 'dismiss'): void {
    this.send({ type: 'suggestion_response', suggestionId, action });
  }

  // Generation event subscriptions
  subscribeToGeneration(sessionId: string, userId: string): void {
    this.send({ type: 'subscribe_generation', sessionId, userId });
  }

  unsubscribeFromGeneration(sessionId: string, userId: string): void {
    this.send({ type: 'unsubscribe_generation', sessionId, userId });
  }
}

// Export singleton instance
export const wsService = new WebSocketService();
export default wsService;

// Hook for React components
export function useWebSocket() {
  return wsService;
}

// Higher-order component wrapper for real-time collaboration
export function withWebSocket<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  options?: { autoConnect?: boolean; sessionId?: string; userId?: string }
) {
  return function WebSocketWrapper(props: P) {
    const [isConnected, setIsConnected] = React.useState(false);

    React.useEffect(() => {
      if (options?.autoConnect) {
        wsService.connect().then(() => {
          setIsConnected(true);
          if (options.sessionId && options.userId) {
            wsService.joinSession(options.sessionId, options.userId, 'User');
          }
        });

        return () => {
          wsService.disconnect();
        };
      }
    }, []);

    return <WrappedComponent {...props} wsConnected={isConnected} wsService={wsService} />;
  };
}